<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, provide, ref, shallowRef, watch } from 'vue';
import { stickyContainerGroupInjectionKey, type StickyScrubberItem } from './internal';

type Entry = {
  sectionEl: HTMLElement;
  labelText: string;
  index: number;
};

/**
 * Events:
 * - `activeIdChange` fires whenever the active-section id changes (observer-driven or via a
 *   programmatic `scrubTo` from the scrubber). Consumers can mirror this into their own
 *   navigation state (e.g. a `NavContext`).
 * - `visibilityChange` fires for each section whose true viewport visibility flips. Driven by
 *   a separate observer with no `rootMargin` so it reflects actual on-screen presence, not the
 *   top-20% band used for active-section selection.
 */
const emit = defineEmits<{
  activeIdChange: [activeId: string | null];
  visibilityChange: [payload: { sectionId: string; isVisible: boolean }];
}>();

const entries = shallowRef<Entry[]>([]);
const activeId = ref<string | null>(null);

/**
 * Tracks which section ids are currently intersecting the top 20% band of the viewport.
 * Updated continuously by the IntersectionObserver, even while a programmatic scroll lock
 * is held, so the next recompute after the lock release sees fresh state.
 */
const intersectingIds = new Set<string>();
/** Tracks true viewport visibility (no `rootMargin`); the source for `visibilityChange` emits. */
const visibleIds = new Set<string>();

/**
 * `true` while a programmatic scroll (click on a scrubber button or `1`–`9` keyboard shortcut)
 * is in flight. Suppresses observer-driven `activeId` updates so the destination button stays
 * marked active during the smooth scroll instead of flickering to interim sections.
 */
let isProgrammaticScroll = false;
/** Cleanup of the currently active scroll lock, if any. Allows re-entrant `scrubTo` calls. */
let releaseLock: (() => void) | null = null;

function sortEntriesByDom(arr: Entry[]): Entry[] {
  return [...arr].sort((a, b) => {
    const pos = a.sectionEl.compareDocumentPosition(b.sectionEl);
    return pos & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });
}

/**
 * Picks the DOM-last section currently intersecting the top-band. Falls back to the first
 * registered section so the scrubber is never left without an active marker once sections exist.
 */
function recomputeActive() {
  const sorted = sortEntriesByDom(entries.value);
  if (sorted.length === 0) {
    activeId.value = null;
    return;
  }
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (intersectingIds.has(sorted[i].sectionEl.id)) {
      activeId.value = sorted[i].sectionEl.id;
      return;
    }
  }
  activeId.value = sorted[0].sectionEl.id;
}

/**
 * Walks up from `el` to find the nearest scrollable ancestor.
 */
function findScrollParent(el: HTMLElement): HTMLElement | null {
  let cur: HTMLElement | null = el.parentElement;
  let firstOverflowParent: HTMLElement | null = null;
  while (cur) {
    const s = getComputedStyle(cur);
    if (/(auto|scroll|overlay)/.test(s.overflowY + s.overflow)) {
      if (!firstOverflowParent) firstOverflowParent = cur;
      if (cur.scrollHeight > cur.clientHeight) return cur;
    }
    cur = cur.parentElement;
  }
  return firstOverflowParent;
}

/** Scroll container resolved at mount; used as IntersectionObserver root and as the reference
 *  for the top-20% marker line in `recomputeActiveFromDom`. `null` falls back to the viewport. */
let scrollRoot: HTMLElement | null = null;

/**
 * Cache-free fallback used on wake-up (`display:none` -> visible): the `IntersectionObserver`
 * cache (`intersectingIds`) can be stale across hidden toggles in some browsers, so we read the
 * section rects directly. "Active" is the DOM-last section whose top has scrolled past the
 * top-20% marker line of the scroll root — same semantics as the band observer, but evaluated
 * synchronously.
 */
function recomputeActiveFromDom() {
  const sorted = sortEntriesByDom(entries.value);
  if (sorted.length === 0) {
    activeId.value = null;
    return;
  }
  const rootRect = scrollRoot?.getBoundingClientRect();
  const rootTop = rootRect?.top ?? 0;
  const rootHeight = scrollRoot?.clientHeight ?? window.innerHeight;
  const markerY = rootTop + rootHeight * 0.2;
  let nextId = sorted[0].sectionEl.id;
  for (const e of sorted) {
    const top = e.sectionEl.getBoundingClientRect().top;
    if (top <= markerY) nextId = e.sectionEl.id;
    else break;
  }
  activeId.value = nextId;
}

function scrollSectionIntoView(sectionEl: HTMLElement) {
  if (!scrollRoot) {
    sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  const rootRect = scrollRoot.getBoundingClientRect();
  const sectionRect = sectionEl.getBoundingClientRect();
  const maxScrollTop = Math.max(0, scrollRoot.scrollHeight - scrollRoot.clientHeight);
  const top = Math.max(
    0,
    Math.min(maxScrollTop, scrollRoot.scrollTop + sectionRect.top - rootRect.top)
  );

  try {
    scrollRoot.scrollTo({ top, behavior: 'auto' });
  } catch {
    // Fall through to the direct assignment below; it is the reliable path for nested panels.
  }
  scrollRoot.scrollTop = top;
}

/**
 * `rootMargin: '0px 0px -80% 0px'` shrinks the observer root to the top 20% of the scroll
 * container. A section is intersecting while its rect overlaps that band, so the DOM-last
 * intersecting section is the one the user is currently reading regardless of section heights.
 *
 * Observers are created in `onMounted` so we can resolve the scroll parent from the DOM —
 * children register on their own mount (which runs before parent mount), so registrations that
 * arrive before observers exist are batched and replayed in `onMounted`.
 */
let bandObserver: IntersectionObserver | null = null;
let visibilityObserver: IntersectionObserver | null = null;

watch(activeId, (id) => emit('activeIdChange', id));

/**
 * Wake-up detection: tracks whether the group's root is currently zero-sized (`display:none`
 * via a parent like `NxTabpanel[hidden]`). The transition from zero -> non-zero size signals
 * "this group just became visible again" — we then recompute `activeId` from the DOM (the
 * `IntersectionObserver` cache can be stale across hidden toggles) and re-emit
 * `activeIdChange` unconditionally so consumers that reset their state on tab change get
 * re-synced even when the id itself didn't actually change.
 */
const groupEl = ref<HTMLDivElement | null>(null);
let wasZeroSize = false;
const wakeObserver = new ResizeObserver((obsEntries) => {
  const entry = obsEntries[0];
  if (!entry) return;
  const isZero = entry.contentRect.width === 0 || entry.contentRect.height === 0;
  if (wasZeroSize && !isZero) {
    recomputeActiveFromDom();
    emit('activeIdChange', activeId.value);
  }
  wasZeroSize = isZero;
});

/**
 * Scrolls to `sectionEl` and locks `activeId` to its id. The lock suppresses observer-driven
 * updates and is released only by an explicit user action — wheel, touch, a scroll-affecting
 * key, or another `scrubTo` call (different button). `scrollend` deliberately does NOT release
 * the lock: when the target section is short and cannot bring its top into the 20% band, the
 * observer would otherwise reassign active to the previous section right after the scroll lands.
 */
function scrubTo(sectionEl: HTMLElement) {
  releaseLock?.();

  activeId.value = sectionEl.id;
  isProgrammaticScroll = true;

  scrollSectionIntoView(sectionEl);

  const userScrollKeys = new Set([
    'PageUp',
    'PageDown',
    'Home',
    'End',
    'ArrowUp',
    'ArrowDown',
    ' ',
  ]);
  const onUserKey = (e: KeyboardEvent) => {
    if (userScrollKeys.has(e.key)) cleanup();
  };
  const cleanup = () => {
    if (!isProgrammaticScroll) return;
    isProgrammaticScroll = false;
    window.removeEventListener('wheel', cleanup);
    window.removeEventListener('touchstart', cleanup);
    window.removeEventListener('keydown', onUserKey);
    releaseLock = null;
    requestAnimationFrame(recomputeActiveFromDom);
  };

  window.addEventListener('wheel', cleanup, { once: true, passive: true });
  window.addEventListener('touchstart', cleanup, { once: true, passive: true });
  window.addEventListener('keydown', onUserKey);
  releaseLock = cleanup;
}

// DOM-sorted projection exposed to the scrubber.
const items = computed<StickyScrubberItem[]>(() => {
  const sorted = sortEntriesByDom(entries.value);
  return sorted.map((e) => ({
    id: e.sectionEl.id,
    labelText: e.labelText,
    index: e.index,
    scrollIntoView: () => scrubTo(e.sectionEl),
  }));
});

provide(stickyContainerGroupInjectionKey, {
  items,
  activeId,
  register(sectionEl, labelText, index) {
    bandObserver?.observe(sectionEl);
    visibilityObserver?.observe(sectionEl);
    const entry: Entry = { sectionEl, labelText, index };
    entries.value = [...entries.value, entry];

    return {
      update(labelText, index) {
        entry.labelText = labelText;
        entry.index = index;
        entries.value = [...entries.value];
      },
      unregister() {
        bandObserver?.unobserve(sectionEl);
        visibilityObserver?.unobserve(sectionEl);
        intersectingIds.delete(sectionEl.id);
        visibleIds.delete(sectionEl.id);
        entries.value = entries.value.filter((x) => x !== entry);
      },
    };
  },
});

/**
 * Keyboard shortcut: pressing `1`–`9` jumps to the section with the matching `index`,
 * unless the keypress is consumed by an editable or focus-trapping element, or a modifier
 * is held. Covers the 1–9 range per spec; extend if more than 9 sections are supported.
 *
 * Editable detection covers native form fields, `contenteditable`, and ARIA composite widgets
 * (`combobox`/`textbox`/`listbox`/`spinbutton`/`searchbox`) plus `dialog` ancestors so that an
 * open custom select or modal does not have its own number-key handling preempted.
 */
const editableSelector =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], ' +
  '[role="combobox"], [role="textbox"], [role="listbox"], [role="spinbutton"], ' +
  '[role="searchbox"], [role="dialog"]';

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return !!t.closest(editableSelector);
}

function onKeydown(e: KeyboardEvent) {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  if (isEditableTarget(e.target)) return;
  if (!/^[1-9]$/.test(e.key)) return;
  const index = Number(e.key);
  const item = items.value.find((i) => i.index === index);
  if (!item) return;
  e.preventDefault();
  item.scrollIntoView();
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  if (groupEl.value) wakeObserver.observe(groupEl.value);

  scrollRoot = groupEl.value ? findScrollParent(groupEl.value) : null;

  bandObserver = new IntersectionObserver(
    (obsEntries) => {
      for (const obsEntry of obsEntries) {
        const id = (obsEntry.target as HTMLElement).id;
        if (obsEntry.isIntersecting) intersectingIds.add(id);
        else intersectingIds.delete(id);
      }
      if (isProgrammaticScroll) return;
      recomputeActive();
    },
    { root: scrollRoot, rootMargin: '0px 0px -80% 0px', threshold: 0 }
  );

  visibilityObserver = new IntersectionObserver(
    (obsEntries) => {
      for (const obsEntry of obsEntries) {
        const id = (obsEntry.target as HTMLElement).id;
        const wasVisible = visibleIds.has(id);
        if (obsEntry.isIntersecting && !wasVisible) {
          visibleIds.add(id);
          emit('visibilityChange', { sectionId: id, isVisible: true });
        } else if (!obsEntry.isIntersecting && wasVisible) {
          visibleIds.delete(id);
          emit('visibilityChange', { sectionId: id, isVisible: false });
        }
      }
    },
    { root: scrollRoot, threshold: 0 }
  );

  // Replay any registrations that landed before the observers existed.
  for (const e of entries.value) {
    bandObserver.observe(e.sectionEl);
    visibilityObserver.observe(e.sectionEl);
  }
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  releaseLock?.();
  bandObserver?.disconnect();
  visibilityObserver?.disconnect();
  wakeObserver.disconnect();
});
</script>

<template>
  <div ref="groupEl" class="nx-sticky-container-group">
    <div class="nx-sticky-container-group-content">
      <slot />
    </div>
    <aside class="nx-sticky-container-group-rail">
      <slot name="scrubber" />
    </aside>
  </div>
</template>

<style scoped>
.nx-sticky-container-group {
  /* Two-column grid: sections take the remaining width, scrubber rail auto-sizes to its content. */
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  position: relative;
  width: 100%;
}

.nx-sticky-container-group-content {
  min-width: 0;
}

.nx-sticky-container-group-rail {
  position: sticky;
  top: 0;
  align-self: start;
  max-height: 100vh;
}
</style>
